/** Administration → Master Data → Honorifics. Governs the global
 *  honorific/salutation dropdown used across ONE NETWORK & contact forms. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, Plus } from "lucide-react";

export default function AdminHonorifics() {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");
  const load = useCallback(async () => {
    try { setRows((await api.get("/one-network/honorifics?all=true")).data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!label.trim()) return;
    try { await api.post("/one-network/honorifics", { label: label.trim() }); setLabel(""); toast.success("Added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/one-network/honorifics/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[720px] mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="admin-honorifics-page">
      <div className="flex items-center gap-2">
        <BadgeCheck className="h-5 w-5 text-slate-700" />
        <h1 className="text-xl font-semibold text-slate-900">Master Data · Honorifics</h1>
      </div>
      <p className="text-[13px] text-slate-500">One governed salutation list used everywhere a contact is created. ONE never guesses a title — users select it.</p>
      <div className="flex items-center gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New honorific (e.g. Capt.)"
          className="h-9 w-56 text-[13px]" data-testid="honorific-new-label" />
        <Button variant="create" size="sm" onClick={add} data-testid="honorific-add"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-2" data-testid={`honorific-row-${r.label}`}>
            <span className="text-[14px] font-medium text-slate-800 flex-1">{r.label}</span>
            <Badge className={`text-[10px] border ${r.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-300"}`}>{r.active ? "ACTIVE" : "INACTIVE"}</Badge>
            <Button size="sm" variant="outline" className="h-7" onClick={() => toggle(r)} data-testid={`honorific-toggle-${r.label}`}>
              {r.active ? "Deactivate" : "Activate"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
